/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, IGridSettings, IImageSettings, Image } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { CartLine, SalesLine } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import React, { useEffect, useState } from 'react';
import { CheckoutPrice } from '../custom-components/checkout-price';
import { Quantity } from '../../utilities/quantity';

/**
 * ICartlineResourceString: Interface for specifying the
 * resource strings that the component needs
 */
export interface ICartlineResourceString {
    /** Display string for discount label */
    discountStringText: string;
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
    /** Display string for quantity label  */
    quantityDisplayString: string;
    /** Display string for quantity label  */
    inputQuantityAriaLabel: string;
    /** Aria label for the decrement button in quanitity component  */
    decrementButtonAriaLabel: string;
    /** Aria label for the increment button in quanitity component  */
    incrementButtonAriaLabel: string;
}

interface ICartLineData {
    cartLine: CartLine | SalesLine;
    product: SimpleProduct;
}

/**
 * ICartLineProps: The props required to render cartLineitem
 */
export interface ICartLineProps extends IComponentProps<ICartLineData> {
    /** The flag to change the quantity component from interactible to static  */
    disableQuantityInteractivity?: boolean;
    /** The primary image url  */
    primaryImageUrl?: string;
    /** The product url  */
    productUrl?: string;
    /** GridSettings for the product image in cartLine  */
    gridSettings: IGridSettings;
    /** ImageSettings for the product image in cartLine  */
    imageSettings: IImageSettings;
    /** Boolean flag to indicate if the item is out of stock  */
    isOutOfStock?: boolean;
    /** Flag to make quantity section editable  */
    isQuantityEditable?: boolean;
    /** Max quantity for line item  */
    maxQuantity?: number;
    /** Current quantity for line item  */
    currentQuantity?: number;
    /** Resource string for the component  */
    resources: ICartlineResourceString;
    /** SalesLine flag  */
    isSalesLine?: boolean;
    /** Error message to show in place of quantity */
    errorMessage?: string;
    /** Quantity onChange callback  */
    quantityOnChange?(cartLine: CartLine, newQuantity: number): void;
}

interface IDimensionStrings {
    /** String for size key  */
    sizeString: string;
    /** String for color key  */
    colorString: string;
    /** String associated with the configuration product dimension  */
    configString: string;
    /** String for style key  */
    styleString: string;
}

const CartLineItemFunctions = {
    renderProductDimensions: (Product: SimpleProduct, DimensionStrings: IDimensionStrings) => {
        const comma: string = ', ';
        if (!Product || !Product.Dimensions) {
            return [];
        }
        return Product.Dimensions.map((productDimension, index) => {
            return (
                <span key={`${Product.RecordId}-${index}-span-ProductDimensions`}>
                    {index !== 1 && (
                        <span className='msc-cart-line__product-variant-color' key={`${Product.RecordId}-${index}ProductDimensions`}>
                            {productDimension.DimensionTypeValue === 1
                                ? `${productDimension.DimensionValue && productDimension.DimensionValue.Value}`
                                : ''}
                        </span>
                    )}
                    {index === 1 && (
                        <span className='msc-cart-line__product-variant-color' key={`${Product.RecordId}-${index}ProductDimensions`}>
                            {productDimension.DimensionTypeValue === 2
                                ? `${comma}${productDimension.DimensionValue && productDimension.DimensionValue.Value}`
                                : productDimension.DimensionTypeValue === 3
                                ? `${DimensionStrings.sizeString}
                                ${productDimension.DimensionValue && productDimension.DimensionValue.Value}`
                                : productDimension.DimensionTypeValue === 4
                                ? `${comma}${DimensionStrings.styleString} ${productDimension.DimensionValue &&
                                      productDimension.DimensionValue.Value}`
                                : ''}
                        </span>
                    )}
                </span>
            );
        });
    }
};

const renderPrice = (props: ICartLineProps): JSX.Element => {
    const { isSalesLine } = props;
    const TotalAmount = props.data.cartLine && props.data.cartLine.TotalAmount ? props.data.cartLine.TotalAmount : 0;
    const quantity = props.data.cartLine && props.data.cartLine.Quantity ? props.data.cartLine.Quantity : 0;
    const netPriceAfterDiscount = TotalAmount !== 0 && quantity !== 0 ? TotalAmount / quantity : 0;
    return (
        <div className='msc-cart-line__product-savings msc-cart-line_product-price col-sm-2'>
            <CheckoutPrice
                data={
                    isSalesLine
                        ? {
                              price: {
                                  // @ts-ignore
                                  CustomerContextualPrice: props.data.cartLine.NetAmount,
                                  BasePrice: props.data.cartLine.Price
                              }
                          }
                        : {
                              price: {
                                  CustomerContextualPrice: netPriceAfterDiscount,
                                  BasePrice: props.data.cartLine.Price
                              }
                          }
                }
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                className='msc-cart-line__product-savings-actual-123'
            />
        </div>
    );
};

const _generateErrorMessage = (props: ICartLineProps): JSX.Element | null => {
    if (props.errorMessage) {
        return (
            <div className='msc-alert__header'>
                <span className='msi-exclamation-triangle' />
                <span>{props.errorMessage}</span>
            </div>
        );
    }

    return null;
};

const _generateQuantityandPrice = (props: ICartLineProps): JSX.Element[] | null => {
    const { resources } = props;
    const mobileView =
        props.context.request && props.context.request.device && props.context.request.device.Type === 'Mobile' ? true : false;
    // @ts-ignore
    const extendedPrice = props.data.cartLine.ExtendedPrice || 0;
    const formattedPrice = props.context.cultureFormatter.formatCurrency(extendedPrice);
    const nodes = [];
    const errorMessage = _generateErrorMessage(props);

    const onChange = (newValue: number) => {
        if (props.quantityOnChange) {
            props.quantityOnChange(props.data.cartLine, newValue);
        }
    };

    if (props.isOutOfStock) {
        if (errorMessage) {
            nodes.push(<div className='msc-alert-danger'>{errorMessage}</div>);
            return nodes;
        }
        return null;
    }

    if (props.isQuantityEditable) {
        if (errorMessage) {
            nodes.push(<div className='msc-alert-danger'>{errorMessage}</div>);
        }
        nodes.push(
            <div className='col-sm-2'>
                <div className='msc-cart-line__product-quantity'>
                    <Quantity
                        id={`msc-cart-line__quantity_${props.data.product.RecordId}`}
                        max={props.maxQuantity || 0}
                        currentCount={props.currentQuantity}
                        onChange={onChange}
                        inputQuantityAriaLabel={resources.inputQuantityAriaLabel}
                        decrementButtonAriaLabel={resources.decrementButtonAriaLabel}
                        incrementButtonAriaLabel={resources.incrementButtonAriaLabel}
                    />
                </div>
            </div>
        );
    } else {
        nodes.push(
            <div className='col-sm-2'>
                <div className='qty-col'>
                    <div className='msc-cart-line__quantity'>
                        <label className='quantity-label'>{resources.quantityDisplayString}:</label>
                        <span className='quantity-value'>{props.data.cartLine.Quantity}</span>
                    </div>

                    {mobileView && formattedPrice}
                </div>
            </div>
        );
    }

    nodes.push(
        <div className='col-sm-2 text-right subtotal-price'>
            <strong>{formattedPrice}</strong>
        </div>
    );

    return nodes;
};

const CartLine: React.FC<ICartLineProps> = (props: ICartLineProps) => {
    const { productUrl, resources } = props;
    const { product } = props.data;
    const [cartProduct, setProduct] = useState<SimpleProduct | undefined>();
    useEffect(() => {
        if (!cartProduct && props.data.product) {
            setProduct(props.data.product);
        }
    });
    const destructDimensionStrings = {
        sizeString: resources.sizeString,
        colorString: resources.colorString,
        styleString: resources.styleString,
        configString: resources.configString
    };
    const productDimensions = cartProduct && CartLineItemFunctions.renderProductDimensions(cartProduct, destructDimensionStrings);
    return (
        <div className='msc-cart-line'>
            <div className='row'>
                <div className='col-sm-6'>
                    <div className='msc-cart-line__product-image'>
                        <Image
                            src={props.primaryImageUrl || ''}
                            altText={product?.Name}
                            gridSettings={props.gridSettings}
                            imageSettings={props.imageSettings}
                            loadFailureBehavior='empty'
                        />
                    </div>
                    <div className='msc-cart-line__content'>
                        <div className='msc-cart-line__product'>
                            <a className='msc-cart-line__product-title' href={productUrl}>
                                {product?.Name}
                            </a>
                            {productDimensions && productDimensions.length > 0 ? (
                                <div className='msc-cart-line__product-variants'>{productDimensions}</div>
                            ) : (
                                ''
                            )}
                            <div className='product-SKU'>
                                <span>Product ID: {product?.ItemId}</span>
                            </div>
                        </div>
                        <div className='msc-cart-line__quantity'>
                            <label className='quantity-label'>{resources.quantityDisplayString}:</label>
                            <span className='quantity-value'>{props.data.cartLine.Quantity}</span>
                        </div>
                    </div>
                </div>
                {renderPrice(props)}
                {_generateQuantityandPrice(props)}
            </div>
        </div>
    );
};

export default CartLine;
